
import React from 'react';
import { Ball, SimulationConfig, MorphData, EscapeRing, BallPiece } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { MS_PER_TICK } from '@/constants/constants';
import { ShapeUtils } from '../physics/ShapeUtils';
import { RhythmicPhysics } from '../modes/RhythmicPhysics';
import { BallBreakAnimation } from '../animations/BallBreakAnimation';

export const RhythmicLogic = {
  init: (
    effectiveCfg: SimulationConfig,
    rhythmicIndexRef: React.MutableRefObject<number>,
    createBall: (overrides: Partial<Ball>) => Ball,
    centerX: number,
    centerY: number,
    getInitialBallState: (index: number, total: number, config?: any) => { pos: { x: number, y: number }, vel: { x: number, y: number } },
    now: number = Date.now()
  ): Ball[] => {
    rhythmicIndexRef.current = 0;
    const total = Math.max(1, effectiveCfg.initialBalls || 1);
    return Array.from({ length: total }).map((_, i) => {
        const { pos, vel } = getInitialBallState(i, total, effectiveCfg.ballPlacementIndividualEnabled ? (effectiveCfg.ballPlacementConfigs as any)?.[i] : null);
        return createBall({ id: `rhythm-ball-${i}`, pos: { x: pos.x + centerX, y: pos.y + centerY }, vel, color: (effectiveCfg.ballPlacementIndividualEnabled ? (effectiveCfg.ballPlacementConfigs as any)?.[i]?.color : null) || (i === 0 ? effectiveCfg.ballColor : (i === 1 ? effectiveCfg.ball2Color : '#ffffff')), mass: effectiveCfg.ballMass || 1, spawnTime: now, curvature: 0, lastBounceTime: undefined, nextBounceDuration: undefined, curveBaseSpeed: undefined, originalAngle: undefined });
    });
  },

  update: (
    b: Ball,
    effectiveCfg: SimulationConfig,
    deltaTime: number,
    centerX: number,
    centerY: number,
    containerRadiusRef: any,
    ballGrowthRateRef: any,
    rhythmicTimeScaleRef: any,
    rhythmicTimeScaleRateRef: any,
    rotRad: number,
    hue: number,
    morphData: MorphData,
    collisionEventsRef: any,
    persistentSpectrumBoostRef: any,
    persistentSpectrumTargetRef: any,
    now: number,
    currentTS: number,
    onShake: (intensity: number) => void,
    triggerBlast: any,
    rhythmicBreakPiecesRef: any,
    rhythmicResetTimeoutRef: any,
    rhythmicIndexRef: any,
    initSimulation: any,
    onBounce?: () => void,
    onPlaylistAdvance?: () => void,
    trackOverrides?: any,
    escapeRingsRef?: React.MutableRefObject<EscapeRing[]>,
    rhythmicEscapeShieldActiveRef?: React.MutableRefObject<boolean>,
    rhythmicEscapeShieldBouncesRef?: React.MutableRefObject<number>
  ) => {
      const melodySpeedValue = Math.max(0.01, (rhythmicTimeScaleRef?.current || 1.0));
      const visualTSValue = effectiveCfg.rhythmicBallSpeedDecoupled ? 1.0 : melodySpeedValue;
      const substeps = 4;
      const dt = deltaTime / substeps;
      const g = effectiveCfg.gravity;
      
      if (isNaN(b.pos.x) || isNaN(b.pos.y) || !isFinite(b.pos.x) || !isFinite(b.pos.y)) {
          b.pos = { x: centerX, y: centerY };
      }
      if (isNaN(b.vel.x) || isNaN(b.vel.y) || !isFinite(b.vel.x) || !isFinite(b.vel.y)) {
          b.vel = { x: 5, y: 5 };
      }

      const oldVelInLoop = { x: b.vel.x, y: b.vel.y };

      const useRGB = effectiveCfg.rgbLighting && effectiveCfg.useBallRGB;
      b.color = useRGB ? `hsl(${hue}, 100%, 50%)` : (effectiveCfg.ballColor || '#ffffff');
      
      const minContainerR = ShapeUtils.getMinRadius(morphData?.from || effectiveCfg.containerShape, morphData?.to || effectiveCfg.containerShape, morphData?.factor || 0, containerRadiusRef.current, effectiveCfg.containerRadius) - (effectiveCfg.containerLineWidth || 4) / 2;
      const maxAllowed = Math.min(effectiveCfg.ballMaxRadius, minContainerR);

      const triggerBreak = () => {
          AUDIO.playSoundEffect(effectiveCfg.rhythmicMaxSizeSound, effectiveCfg, 1.0, true);
          b.isBreaking = true; b.breakStartTime = now;
          triggerBlast({ pos: { x: centerX, y: centerY }, color: b.color, radius: minContainerR }, minContainerR, 0, morphData, effectiveCfg.rhythmicOuterLayerAnimation || 'EXPLOSION');
          if (effectiveCfg.rhythmicBreakEffect !== 'NONE') {
              rhythmicBreakPiecesRef.current = BallBreakAnimation.createPieces(b.pos, b.radius, b.color, effectiveCfg.rhythmicBreakPieceCount, b.shape || 'CIRCLE', now, effectiveCfg.rhythmicBreakDuration);
          }
          rhythmicResetTimeoutRef.current = setTimeout(() => { initSimulation(); rhythmicResetTimeoutRef.current = null; }, effectiveCfg.rhythmicBreakResetDelay);
      };

      if (effectiveCfg.ballGrowthMode === 'TIME') {
          const nextRadius = b.radius + ballGrowthRateRef.current * (deltaTime / 1000);
          const minAllowed = effectiveCfg.ballMinRadius || 1;
          if (b.radius < maxAllowed && nextRadius >= maxAllowed) triggerBreak();
          b.radius = Math.max(minAllowed, Math.min(maxAllowed, nextRadius));
          if (effectiveCfg.ballGrowthAcceleration !== 0) ballGrowthRateRef.current += effectiveCfg.ballGrowthAcceleration * (deltaTime / 1000);
      }

      for(let s=0; s<substeps; s++) {
          b.vel.y += g * (dt / MS_PER_TICK) * visualTSValue;
          const oldVelForPhysics = { ...b.vel };
          b.pos.x += b.vel.x * (dt / MS_PER_TICK) * visualTSValue; b.pos.y += b.vel.y * (dt / MS_PER_TICK) * visualTSValue;
          
          const relX = b.pos.x - centerX, relY = b.pos.y - centerY;
          const angle = Math.atan2(relY, relX), dist = Math.sqrt(relX*relX + relY*relY);
          const lineWidth = effectiveCfg.containerLineWidth || 4;
          
          const hasEscapeRings = effectiveCfg.enableEscape && escapeRingsRef?.current && escapeRingsRef.current.length > 0;
          const shieldActive = rhythmicEscapeShieldActiveRef?.current;
          const shieldRadius = effectiveCfg.rhythmicEscapeShieldRingRadius || 50;

          // Determine if we are hitting the shield or a ring
          let targetRForSync = 200;
          if (typeof containerRadiusRef === 'object' && containerRadiusRef !== null) {
              targetRForSync = (containerRadiusRef as any).current;
          } else if (typeof containerRadiusRef === 'number') {
              targetRForSync = containerRadiusRef;
          }
          if (isNaN(targetRForSync) || !isFinite(targetRForSync)) targetRForSync = effectiveCfg.containerRadius || 200;
          let isShieldHit = false;

          if (hasEscapeRings) {
              // Shield always takes priority if active
              if (shieldActive) {
                  targetRForSync = shieldRadius;
                  isShieldHit = true;
              } else {
                  targetRForSync = escapeRingsRef.current[0].radius;
              }
          }

          const effectiveShapeR = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, morphData.from, morphData.to, morphData.factor, targetRForSync, morphData.baseRadius) - lineWidth / 2;
          
          if (dist + b.radius >= effectiveShapeR && (b.vel.x * relX + b.vel.y * relY > 0 || (b.vel.x === 0 && b.vel.y === 0))) { 
              // Check for gap if it's an escape ring (not a shield)
              let inGap = false;
              if (hasEscapeRings && !isShieldHit) {
                  const ring = escapeRingsRef.current[0];
                  const originalIndex = effectiveCfg.escapeRingCount - escapeRingsRef.current.length;
                  const isOpposite = originalIndex < (effectiveCfg.escapeOppositeGapCount || 0);
                  const targetGapSize = isOpposite ? (effectiveCfg.escapeOppositeGapSize || 30) : effectiveCfg.escapeGapSize;
                  let gapWidth = (targetGapSize * Math.PI) / 180;
                  if (effectiveCfg.escapeGapAlignment === 'FIXED_SIZE') gapWidth = ((targetGapSize * Math.PI / 180) * effectiveCfg.containerRadius) / ring.radius;
                  const halfGap = gapWidth / 2;
                  
                  let normAngle = angle;
                  if (normAngle < 0) normAngle += Math.PI * 2;
                  let ringStart = ring.angle % (Math.PI * 2);
                  if (ringStart < 0) ringStart += Math.PI * 2;
                  let angleDiff = (normAngle - ringStart + Math.PI) % (Math.PI * 2);
                  if (angleDiff < 0) angleDiff += Math.PI * 2;
                  angleDiff -= Math.PI;
                  const diff = Math.abs(angleDiff);
                  const ballAngularWidth = b.radius / ring.radius;
                  inGap = diff < (halfGap - ballAngularWidth * 0.8);
              }

              const speed = Math.sqrt(b.vel.x*b.vel.x + b.vel.y*b.vel.y) * visualTSValue;
              const timeSinceCollisionMs = ((dist + b.radius - effectiveShapeR) / Math.max(speed, 0.001)) * MS_PER_TICK;
              const exactCollisionTime = AUDIO.initCtx().currentTime - (timeSinceCollisionMs / 1000) + (effectiveCfg.rhythmicSyncOffset / 1000);

              const localNormal = ShapeUtils.getNormal({ x: relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad), y: relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad) }, morphData.from, targetRForSync - lineWidth / 2, morphData.to, morphData.factor, morphData.baseRadius);
              const worldNormal = { x: localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad), y: localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad) };
              
              const step = AUDIO.getNextRhythmicStep(effectiveCfg.selectedMelody, effectiveCfg, rhythmicIndexRef.current, trackOverrides);

              if (!inGap) {
                  b.justBounced = true;
                  b.pos.x -= worldNormal.x * (dist + b.radius - effectiveShapeR + 2.0); 
                  b.pos.y -= worldNormal.y * (dist + b.radius - effectiveShapeR + 2.0);

                  if (effectiveCfg.rhythmicTimeScaleIncreaseMode === 'COLLISION') {
                      rhythmicTimeScaleRef.current += (rhythmicTimeScaleRateRef.current);
                      if (effectiveCfg.rhythmicTimeScaleAcceleration !== 0) {
                          rhythmicTimeScaleRateRef.current += effectiveCfg.rhythmicTimeScaleAcceleration;
                      }
                  }
              } else {
                  // PASSING THROUGH GAP in Rhythmic mode
                  if (!isShieldHit && escapeRingsRef?.current) {
                      const ringCount = escapeRingsRef.current.length;
                      if (ringCount > 0) {
                          const ring = escapeRingsRef.current[0];
                          const ringColor = `hsl(${(hue + ring.hue) % 360}, 100%, 60%)`;
                          if (triggerBlast) {
                              triggerBlast({ pos: { x: b.pos.x, y: b.pos.y }, color: ringColor, radius: ring.radius }, ring.radius, 0, morphData, effectiveCfg.escapeBreakAnimation || 'SHATTER');
                          }
                          escapeRingsRef.current.shift();
                      }
                      
                      // After shifting, if there are more rings, aim for the next one immediately
                      const remainingTimeSec = Math.max(0.005, (Math.max(10, step.totalDuration) / Math.max(0.01, melodySpeedValue)) / 1000 - (timeSinceCollisionMs / 1000));
                      const T_val = Math.max(0.1, remainingTimeSec / (MS_PER_TICK / 1000));
                      if (escapeRingsRef.current.length > 0) {
                          const nextRing = escapeRingsRef.current[0];
                          const bounceResult = RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, nextRing.radius, b.radius, morphData, rotRad, visualTSValue, melodySpeedValue, Math.max(10, step.totalDuration), T_val, timeSinceCollisionMs, now, g, substeps, oldVelInLoop, worldNormal, step.notesToPlay.length);
                          const safeT_bounce = Math.max(0.1, bounceResult.T);
                          b.vel.x = bounceResult.VX / safeT_bounce;
                          b.vel.y = bounceResult.VY / safeT_bounce;
                          b.nextBounceDuration = bounceResult.T;
                      } else {
                          // No more rings, aim for outer container (if any) or infinity
                          const bounceResult = RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, effectiveCfg.containerRadius, b.radius, morphData, rotRad, visualTSValue, melodySpeedValue, Math.max(10, step.totalDuration), T_val, timeSinceCollisionMs, now, g, substeps, oldVelInLoop, worldNormal, step.notesToPlay.length);
                          const safeT_bounce = Math.max(0.1, bounceResult.T);
                          b.vel.x = bounceResult.VX / safeT_bounce;
                          b.vel.y = bounceResult.VY / safeT_bounce;
                          b.nextBounceDuration = bounceResult.T;
                      }
                  }
              }

              AUDIO.playRhythmicStep(step.notesToPlay, effectiveCfg, exactCollisionTime, melodySpeedValue);
              rhythmicIndexRef.current = step.nextIndex;
              if (step.playlistAdvance && onPlaylistAdvance) onPlaylistAdvance();
              if (onBounce) {
                  onBounce();
                  // Handle auto-disable borders
                  if (effectiveCfg.escapeAutoDisableBordersEnabled && effectiveCfg.escapeShowOuterBorders) {
                      const bounces = (b.bounceCount || 0) + 1;
                      if (bounces >= (effectiveCfg.escapeAutoDisableBordersBounces || 10)) {
                          b.shouldDisableBorders = true; 
                      }
                  }
              }
              
              collisionEventsRef.current.push({ 
                  id: `col-${now}-${b.id}-${s}`, 
                  pos: { x: b.pos.x + worldNormal.x * b.radius, y: b.pos.y + worldNormal.y * b.radius }, 
                  startTime: now - timeSinceCollisionMs, 
                  radius: b.radius * 2.5 * (effectiveCfg.collisionEffectScale || 1.0) * (effectiveCfg.rhythmicCircleScale || 1.0), 
                  color: b.color, 
                  effectType: effectiveCfg.collisionEffect, 
                  duration: effectiveCfg.collisionEffectDuration || 800 
              });

              if (effectiveCfg.rhythmicSpectrumEnabled) {
                  const hitAngle = Math.atan2(relY, relX);
                  const centerIdx = Math.floor(((hitAngle + Math.PI) / (2 * Math.PI)) * 256) % 256;
                  const spread = effectiveCfg.rhythmicSpectrumCollisionSpread || 10;
                  const intensity = effectiveCfg.rhythmicSpectrumCollisionIntensity || 1.0;
                  for (let j = -spread; j <= spread; j++) {
                      const idx = (centerIdx + j + 256) % 256;
                      const factor = 1 - Math.abs(j) / (spread + 1);
                      persistentSpectrumTargetRef.current[idx] = Math.max(persistentSpectrumTargetRef.current[idx] || 0, intensity * factor);
                  }
              }

              onShake(isShieldHit ? 10 : 5);
              
              if (isShieldHit && effectiveCfg.rhythmicEscapeShieldRingDestroyMode === 'BOUNCES') {
                  if (rhythmicEscapeShieldBouncesRef) rhythmicEscapeShieldBouncesRef.current++;
                  if (rhythmicEscapeShieldBouncesRef && rhythmicEscapeShieldBouncesRef.current >= (effectiveCfg.rhythmicEscapeShieldRingDestroyValue || 20)) {
                      if (rhythmicEscapeShieldActiveRef) rhythmicEscapeShieldActiveRef.current = false;
                      triggerBlast({ pos: { x: centerX, y: centerY }, color: '#ffffff', radius: shieldRadius }, shieldRadius, 0, morphData, effectiveCfg.rhythmicEscapeShieldBreakAnimation || 'SUPERNOVA');
                  }
              }

              if (effectiveCfg.ballGrowthMode === 'COLLISION') {
                  const growth = ballGrowthRateRef.current;
                  const minAllowed = effectiveCfg.ballMinRadius || 1;
                  if (b.radius < maxAllowed && b.radius + growth >= maxAllowed) { triggerBreak(); return; }
                  b.radius = Math.max(minAllowed, Math.min(maxAllowed, b.radius + growth));
              }

              if (!inGap || isShieldHit) {
                  const songStep = step;

                  if (songStep && effectiveCfg.rhythmicDeterministicBounce !== false) {
                      const remainingTimeSec = Math.max(0.005, (Math.max(10, songStep.totalDuration) / Math.max(0.01, melodySpeedValue)) / 1000 - (timeSinceCollisionMs / 1000));
                      const T_aim = Math.max(0.1, remainingTimeSec / (MS_PER_TICK / 1000));
                      
                      const nextRadius = (hasEscapeRings && !isShieldHit && escapeRingsRef.current.length > 1) 
                          ? escapeRingsRef.current[1].radius 
                          : targetRForSync;

                      const bounceResult = RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, nextRadius, b.radius, morphData, rotRad, visualTSValue, melodySpeedValue, Math.max(10, songStep.totalDuration), T_aim, timeSinceCollisionMs, now, g, substeps, oldVelInLoop, worldNormal, songStep.notesToPlay.length);
                      const safeT_bounce = Math.max(0.1, bounceResult.T);
                      b.vel.x = bounceResult.VX / safeT_bounce;
                      b.vel.y = bounceResult.VY / safeT_bounce;
                      b.nextBounceDuration = bounceResult.T;
                  } else {
                      // Standard reflection fallback
                      const dot = oldVelInLoop.x * worldNormal.x + oldVelInLoop.y * worldNormal.y;
                      if (dot > 0) {
                          b.vel.x = oldVelInLoop.x - 2 * dot * worldNormal.x;
                          b.vel.y = oldVelInLoop.y - 2 * dot * worldNormal.y;
                      }
                      b.vel.x *= effectiveCfg.restitution;
                      b.vel.y *= effectiveCfg.restitution;
                      
                      if (songStep) rhythmicIndexRef.current = songStep.nextIndex;
                      
                      // Apply randomization
                      if (effectiveCfg.bounceAngleRandomization > 0) {
                          const currentSpeed = Math.sqrt(b.vel.x * b.vel.x + b.vel.y * b.vel.y);
                          if (currentSpeed > 0.1) {
                              const rand = effectiveCfg.bounceAngleRandomization;
                              const maxDev = Math.PI * 0.5 * rand;
                              const dev = (Math.random() * 2 - 1) * maxDev;
                              const angle = Math.atan2(b.vel.y, b.vel.x) + dev;
                              const newVX = Math.cos(angle) * currentSpeed;
                              const newVY = Math.sin(angle) * currentSpeed;
                              if (newVX * worldNormal.x + newVY * worldNormal.y < -0.1) {
                                  b.vel.x = newVX;
                                  b.vel.y = newVY;
                              }
                          }
                      }
                      b.pos.x += worldNormal.x * 2.0;
                      b.pos.y += worldNormal.y * 2.0;
                  }
              } else {
                  const remainingTimeSec = Math.max(0.005, (Math.max(10, step.totalDuration) / Math.max(0.01, melodySpeedValue)) / 1000 - (timeSinceCollisionMs / 1000));
                  const T = Math.max(0.1, remainingTimeSec / (MS_PER_TICK / 1000));
                  const nextRadius = (hasEscapeRings && escapeRingsRef.current.length > 1) 
                      ? escapeRingsRef.current[1].radius 
                      : targetRForSync;
                  
                  const bounceResult = RhythmicPhysics.calculateNextBounce(b, effectiveCfg, centerX, centerY, nextRadius, b.radius, morphData, rotRad, visualTSValue, melodySpeedValue, Math.max(10, step.totalDuration), T, timeSinceCollisionMs, now, g, substeps, b.vel, {x:0, y:0}, step.notesToPlay.length);
                  const safeT_bounce = Math.max(0.1, bounceResult.T);
                  b.vel.x = bounceResult.VX / safeT_bounce;
                  b.vel.y = bounceResult.VY / safeT_bounce;
                  b.nextBounceDuration = bounceResult.T;
              }
          }
      }
  }
};
